'use client'
import {
  ExpandableBenfitsList_default
} from "./chunk-6RPHG7BM.mjs";
import {
  useSubscription
} from "./chunk-NAUELPNF.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/components/plans/FirstAvailablePlan.tsx
import { useMemo, useState } from "react";
import { useGetSubscriptionPlans } from "@wbk/api/hooks";
import { CurrencyPriceLabel } from "@wbk/currency";
import { Button, ToggleGroup } from "@wbk/ui";
import { twMerge } from "tailwind-merge";
import { useRouter, useTranslation } from "@wbk/config";

// src/components/spinner/PlanSkeleton.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var PlanSkeleton = () => {
  return /* @__PURE__ */ jsx("div", { className: "mini-scrollbar relative overflow-y-auto pb-20", children: /* @__PURE__ */ jsxs("div", { className: "space-y-8 pt-6", children: [
    /* @__PURE__ */ jsx("div", { className: "bg-body-dark/10 h-10 animate-pulse rounded-md" }),
    /* @__PURE__ */ jsx("div", { className: "bg-body-dark/10 h-8 animate-pulse rounded-md" }),
    /* @__PURE__ */ jsx("div", { className: "bg-body-dark/10 h-80 animate-pulse rounded-md" })
  ] }) });
};
var PlanSkeleton_default = PlanSkeleton;

// src/components/plans/FirstAvailablePlan.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var FirstAvailablePlan = ({
  header,
  title,
  subtitle,
  className,
  planGroupId,
  onPlanChange,
  onConfirm
}) => {
  const { slug, active } = useSubscription();
  const { locale } = useRouter();
  const { t } = useTranslation("subscription_common");
  const [selectedPlan, setSelectedPlan] = useState();
  const { data, isLoading, error } = useGetSubscriptionPlans(
    {
      lang: locale,
      subscriptionSlug: slug
    },
    { enabled: active }
  );
  const firstPlan = useMemo(() => {
    var _a;
    return (_a = data == null ? void 0 : data.plan_groups) == null ? void 0 : _a.find((group) => group._id === planGroupId);
  }, [data == null ? void 0 : data.plan_groups, planGroupId]);
  if (isLoading) {
    return /* @__PURE__ */ jsx2(PlanSkeleton_default, {});
  }
  if (!firstPlan || error) {
    return /* @__PURE__ */ jsx2("div", { className: "space-y-4 pb-20", children: /* @__PURE__ */ jsx2("p", { className: "text-text-secondary py-6 text-center", children: (error == null ? void 0 : error.message) || t("subscription_common:no_subscriptions") }) });
  }
  const icon = firstPlan == null ? void 0 : firstPlan.plans[0].icon;
  const benefits = firstPlan == null ? void 0 : firstPlan.plans[0].benefits;
  return /* @__PURE__ */ jsxs2("div", { className: twMerge("space-y-6", className), children: [
    header != null ? header : /* @__PURE__ */ jsxs2(Fragment, { children: [
      /* @__PURE__ */ jsx2("h2", { className: "text-2xl font-semibold", children: t("subscription_common:upgrade_subscription") }),
      /* @__PURE__ */ jsx2("hr", { className: "border-paper" })
    ] }),
    title && /* @__PURE__ */ jsx2("p", { className: "text-xl font-semibold", children: title }),
    subtitle && /* @__PURE__ */ jsx2("p", { children: subtitle }),
    /* @__PURE__ */ jsxs2("div", { className: "bg-text/15 overflow-hidden rounded-xl", children: [
      /* @__PURE__ */ jsxs2("div", { className: "relative space-y-2 p-4 lg:p-6", children: [
        (firstPlan == null ? void 0 : firstPlan.header_background_image) && /* @__PURE__ */ jsx2(
          "div",
          {
            className: "absolute inset-0 h-full w-full bg-cover bg-no-repeat rtl:-scale-x-100",
            style: { backgroundImage: `url(${(firstPlan == null ? void 0 : firstPlan.header_background_image) || ""})` }
          }
        ),
        /* @__PURE__ */ jsxs2("div", { className: "relative z-10 flex items-center gap-2", children: [
          icon && /* @__PURE__ */ jsx2("img", { src: icon, alt: firstPlan.name, width: 48, height: 48, className: "h-11 w-11" }),
          /* @__PURE__ */ jsx2("p", { className: "text-2xl font-semibold", children: firstPlan == null ? void 0 : firstPlan.name })
        ] })
      ] }),
      /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
        /* @__PURE__ */ jsx2(
          ExpandableBenfitsList_default,
          {
            benefits: benefits.map((b) => __spreadProps(__spreadValues({}, b), {
              name: b.benefit_action_description || ""
            })),
            riyadhSeason: true,
            groupClassName: "lg:p-6 p-4"
          }
        ),
        firstPlan.emoji_image && /* @__PURE__ */ jsx2(
          "img",
          {
            src: firstPlan.emoji_image,
            className: "absolute -bottom-14 h-28 shrink-0 ltr:-right-2 rtl:-left-2 rtl:-scale-x-100",
            alt: ""
          }
        )
      ] }),
      /* @__PURE__ */ jsx2("hr", { className: "border-paper" }),
      /* @__PURE__ */ jsxs2("div", { className: "space-y-3 p-4 lg:p-6 lg:pt-4", children: [
        /* @__PURE__ */ jsx2(
          ToggleGroup,
          {
            name: "billing_options",
            type: "single",
            phill: true,
            className: "data-[state=on]:border-action ring-action border-text/30 grow-0 basis-44 px-3 py-2 data-[state=on]:bg-transparent data-[state=on]:ring-1",
            label: /* @__PURE__ */ jsx2("p", { className: "pb-1", children: t("subscription_common:select_billing_option") }),
            options: firstPlan == null ? void 0 : firstPlan.plans.map((plan) => ({
              value: plan._id,
              text: /* @__PURE__ */ jsxs2("div", { className: "flex items-baseline gap-1", children: [
                /* @__PURE__ */ jsx2(
                  CurrencyPriceLabel,
                  {
                    originalPrice: plan.price + plan.vat,
                    currency: plan.currency
                  }
                ),
                /* @__PURE__ */ jsx2("span", { className: "text-text-secondary text-sm", children: `/ ${plan.payment_terms}` })
              ] })
            })),
            onChange: (planId) => {
              const plan = firstPlan == null ? void 0 : firstPlan.plans.find((plan2) => plan2._id === planId);
              if (plan) {
                setSelectedPlan(plan);
                if (onPlanChange) {
                  onPlanChange(plan);
                }
              }
            }
          }
        ),
        onConfirm && /* @__PURE__ */ jsx2("div", { className: "pt-2", children: /* @__PURE__ */ jsx2(
          Button,
          {
            theme: "action",
            className: "h-11 w-full",
            onClick: () => {
              onConfirm(firstPlan, selectedPlan || firstPlan.plans[0]);
            },
            "data-testid": "subscription_upgrade_confirm_button",
            children: t("subscription_common:upgrade_and_continue")
          }
        ) })
      ] })
    ] })
  ] });
};
var FirstAvailablePlan_default = FirstAvailablePlan;

export {
  FirstAvailablePlan_default
};
//# sourceMappingURL=chunk-32AVF4GI.mjs.map